import { HTMLAttributes, useState, useEffect, useMemo } from "react";
import TechniqueFallback from "@/assets/technique.png";
import {
  installFlowLocationName,
  installFlowManufacturer,
  installFlowAssetType,
  installFlowSerialNumber,
  installFlowTechniquePhoto,
  installFlowOrganizationId,
  installFlowLocationId,
  installFlowSublocationId,
  installFlowModelId,
  installFlowAssetName,
  installFlowInstallationId,
} from "@/installFlow/storage-keys";
import { Button } from "../ui/button";
import { CommonFlowFrame } from "./common-flow-frame";
import { useNavigate } from "@tanstack/react-router";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "../ui/input";
import { cn } from "@/lib/utils";
import { HelpCircle, Loader2 } from "lucide-react";
import { apiClient } from "@/api/client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function useIsTouchDevice(): boolean {
  return useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(hover: none)").matches,
    [],
  );
}

function AssetNameHint() {
  const isTouch = useIsTouchDevice();
  const content = "This name helps you identify the unit: location + make + type + last 4 serial characters";

  if (isTouch) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="focus:outline-none">
            <HelpCircle className="w-4 h-4 mr-1 text-blue-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" className="max-w-xs text-sm">
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="w-4 h-4 mr-1 text-blue-500 cursor-default" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const formSchema = z.object({
  assetName: z.string().min(1, { message: "You must give this asset name" }),
});

function buildDefaultAssetName(): string {
  const locationName = (installFlowLocationName.get() ?? "")
    .replace(/ /g, "-")
    .trim();
  const manufacturer = (installFlowManufacturer.get() ?? "").trim();
  const assetType = (installFlowAssetType.get() ?? "").trim();
  const serial = (installFlowSerialNumber.get() ?? "").trim();
  const last4 = serial.slice(-4);

  return [locationName, manufacturer, assetType, last4]
    .filter(Boolean)
    .join("-")
    .toUpperCase();
}

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function AssetName({ className }: AuthProps) {
  const [Technique, setTechnique] = useState<string>(TechniqueFallback);
  const [isAssetNameFocused, setIsAssetNameFocused] = useState(false);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const defaultAssetName = useMemo(() => buildDefaultAssetName(), []);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assetName: defaultAssetName,
    },
  });

  const assetNameValue = form.watch("assetName");
  // Show warning immediately when empty; hide once value has content (length > 0 after trim)
  const showEmptyWarning = assetNameValue.trim().length === 0;

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    const stored = installFlowTechniquePhoto.get();
    if (stored) setTechnique(stored);
  }, []);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    setApiError(null);

    const organizationId = installFlowOrganizationId.get();
    const locationId = installFlowLocationId.get();
    const sublocationId = installFlowSublocationId.get();
    const serialNumber = installFlowSerialNumber.get() ?? "";
    const modelId = installFlowModelId.get();

    try {
      installFlowAssetName.set(values.assetName);

      const res = await apiClient.post(
        `organizations/${organizationId}/locations/${locationId}/sublocations/${sublocationId}/installations/`,
        {
          name: values.assetName,
          sublocation: Number(sublocationId),
          location: Number(locationId),
          serial_number: serialNumber,
          equipment_model_id: modelId ? Number(modelId) : undefined,
          status: "active",
        },
      );
      installFlowInstallationId.set(String(res.data.id));

      await navigate({ to: "/installFlow/previous-tutorial" });
    } catch {
      setApiError("Failed to save the installation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <CommonFlowFrame>
      <div className="w-full  h-full flex flex-col gap-6">
        <div className="text-left">
          <h2 className="text-xl font-bold text-black-700">
            We gave this asset a name.
          </h2>
          <p className="text-md font-semibold text-gray-600">
            You may edit this, but we recommend keeping this name to help
            identify assets more easily.
          </p>
        </div>
        <div className="w-full flex flex-col h-1/2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-6 w-[350px]">
                <div className="grid gap-4">
                  <FormField
                    control={form.control}
                    name="assetName"
                    render={({ field }) => (
                      <FormItem className="gap-4 flex flex-col">
                        <FormLabel>Asset Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Asset Name"
                            className="h-12 !mt-0"
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value.toUpperCase());
                              form.trigger("assetName");
                            }}
                            onFocus={() => setIsAssetNameFocused(true)}
                            onBlur={() => {
                              field.onBlur();
                              setIsAssetNameFocused(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                form.handleSubmit(onSubmit)();
                              }
                            }}
                          />
                        </FormControl>
                        {showEmptyWarning && (
                          <p className="text-sm font-medium text-destructive !mt-0">
                            You must give this asset a name
                          </p>
                        )}
                      </FormItem>
                    )}
                  />
                  <Button
                    variant="secondary"
                    type="button"
                    className={cn(
                      "w-full h-[42px] p-4 items-center justify-center rounded-[25px]",
                      !isAssetNameFocused && "hidden",
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => form.setValue("assetName", defaultAssetName, { shouldValidate: true })}
                  >
                    Reset to default
                  </Button>
                  <div className="text-sm font-medium text-gray-600 flex gap-2 items-center">
                    <AssetNameHint />
                    Learn more about asset names
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="installFlow"
                    type="submit"
                    disabled={!form.formState.isValid || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Next"
                    )}
                  </Button>
                </div>
                {apiError && (
                  <p className="text-sm font-medium text-destructive">
                    {apiError}
                  </p>
                )}
                <p
                  className={cn(
                    "text-sm font-medium text-destructive",
                    className,
                  )}
                >
                  {form.formState.errors.root?.message}
                </p>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </CommonFlowFrame>
  );
}
